import { ISchedule } from '@app/shared/models/schedule.interface';
import { WeekdaySelection } from '@app/shared/models/weekday-selection';
import { Rule } from '@app/shared/models/rule';
import { IScheduleDto } from '@app/shared/models/schedule-dto';
import { FormGroup, Validators } from '@angular/forms';

export class Schedule implements ISchedule {
  name?: string;
  startTime: string;
  endTime: string;
  days?: WeekdaySelection;
  rules: Rule[];
  enabled: boolean;
  rank: number;
  id?: number;
  active: boolean;
  alwaysActive: boolean;
  startDate?: Date;
  endDate?: Date;
  editable?: boolean;
  timezone: string;
  startSolarEvent: SolarEventType;
  endSolarEvent: SolarEventType;
  startOffset: { value: string; offset: string };
  endOffset: { value: string; offset: string };
  nextRunStartTime: string;
  nextRunEndTime: string;

  constructor(params: ISchedule = {} as ISchedule) {
    this.name = params.name;
    this.startTime = params.startTime || '00:00';
    this.endTime = params.endTime || '00:00';
    this.days = params.days;
    this.rules = params.rules || [];
    this.enabled = params.enabled !== null && params.enabled !== undefined ? params.enabled : true;
    this.rank = params.rank;
    this.id = params.id;
    this.active = params.active || false;
    this.alwaysActive = params.alwaysActive || false;
    this.startDate = params.startDate;
    this.endDate = params.endDate;
    this.editable = params.editable;
    this.timezone = params.timezone;
    this.startSolarEvent = params.startSolarEvent;
    this.endSolarEvent = params.endSolarEvent;
    this.startOffset = params.startOffset || { value: '00', offset: null };
    this.endOffset = params.endOffset || { value: '00', offset: null };
    this.nextRunStartTime = params.nextRunStartTime;
    this.nextRunEndTime = params.nextRunEndTime;
  }

  static clone(other: Schedule): Schedule {
    return new this({
      name: other.name,
      startTime: other.startTime,
      endTime: other.endTime,
      days: WeekdaySelection.clone(other.days),
      rules: other.rules.map((rule) => Rule.clone(rule)),
      enabled: other.enabled,
      rank: other.rank,
      id: other.id,
      active: other.active,
      alwaysActive: other.alwaysActive,
      startDate: other.startDate,
      endDate: other.endDate,
      editable: other.editable,
      timezone: other.timezone,
      startSolarEvent: other.startSolarEvent,
      endSolarEvent: other.endSolarEvent,
      startOffset: other.startOffset,
      endOffset: other.endOffset,
      nextRunStartTime: other.nextRunStartTime,
      nextRunEndTime: other.nextRunEndTime
    });
  }

  static fromDto(scheduleDto: IScheduleDto): ISchedule {
    return {
      name: scheduleDto.name,
      startTime: scheduleDto.startTime,
      endTime: scheduleDto.endTime,
      days: WeekdaySelection.clone(scheduleDto.days),
      rules: scheduleDto.rules.map((rule) => Rule.clone(rule)),
      enabled: scheduleDto.enabled,
      rank: scheduleDto.rank,
      id: scheduleDto.id,
      active: scheduleDto.active,
      alwaysActive: scheduleDto.alwaysActive,
      startDate: scheduleDto.startDate ? this.toDateFromString(scheduleDto.startDate) : null,
      endDate: scheduleDto.endDate ? this.toDateFromString(scheduleDto.endDate) : null,
      editable: scheduleDto.editable,
      timezone: scheduleDto.timezone,
      startSolarEvent: scheduleDto.startSolarEvent || null,
      endSolarEvent: scheduleDto.endSolarEvent || null,
      startOffset: {
        value: scheduleDto.startOffset?.substring(1) || '00',
        offset: scheduleDto.startOffset?.startsWith('+')
          ? 'add'
          : scheduleDto.startOffset?.startsWith('-')
          ? 'minus'
          : null
      },
      endOffset: {
        value: scheduleDto.endOffset?.substring(1) || '00',
        offset: scheduleDto.endOffset?.startsWith('+') ? 'add' : scheduleDto.endOffset?.startsWith('-') ? 'minus' : null
      },
      nextRunStartTime: scheduleDto.nextRunStartTime || null,
      nextRunEndTime: scheduleDto.nextRunEndTime || null
    };
  }

  static toDto(schedule: ISchedule): IScheduleDto {
    return {
      name: schedule.name,
      startTime: schedule.startTime,
      endTime: schedule.endTime,
      days: WeekdaySelection.clone(schedule.days),
      rules: schedule.rules.map((rule) => Rule.clone(rule)),
      enabled: schedule.enabled,
      rank: schedule.rank,
      id: schedule.id,
      active: schedule.active,
      alwaysActive: schedule.alwaysActive,
      startDate: schedule.startDate ? this.toDateString(schedule.startDate) : null,
      endDate: schedule.endDate ? this.toDateString(schedule.endDate) : null,
      editable: schedule.editable,
      timezone: schedule.timezone,
      startSolarEvent: schedule.startSolarEvent || null,
      endSolarEvent: schedule.endSolarEvent || null,
      startOffset: schedule.startOffset?.offset
        ? `${schedule.startOffset.offset === 'add' ? '+' : '-'}${schedule.startOffset.value}`
        : null,
      endOffset: schedule.endOffset?.offset
        ? `${schedule.endOffset.offset === 'add' ? '+' : '-'}${schedule.endOffset.value}`
        : null,
      nextRunStartTime: null,
      nextRunEndTime: null
    };
  }

  static toDateFromString(dateString: string): Date {
    return dateString != null ? new Date(dateString) : null;
  }
  static toDateString(date: Date): string {
    return (
      date.getFullYear() +
      '-' +
      (date.getMonth() + 1).toString().padStart(2, '0') +
      '-' +
      date.getDate().toString().padStart(2, '0')
    );
  }
}

export type ScheduleType = 'WEEKDAY' | 'DATED';
export type SolarEventType = 'SUNRISE' | 'SUNSET';
export const SolarEventOptions = [
  { label: 'Sunrise', value: 'SUNRISE' },
  { label: 'Sunset', value: 'SUNSET' }
];

export const AtSameTime = { label: 'At time of event', time: { value: '00', offset: null } };
export const OffsetBeforeTimes = [
  { label: '10 minutes before', time: { value: '10', offset: 'minus' } },
  { label: '20 minutes before', time: { value: '20', offset: 'minus' } },
  { label: '30 minutes before', time: { value: '30', offset: 'minus' } },
  { label: '40 minutes before', time: { value: '40', offset: 'minus' } },
  { label: '50 minutes before', time: { value: '50', offset: 'minus' } }
];
export const OffsetAfterTimes = [
  { label: '10 minutes after', time: { value: '10', offset: 'add' } },
  { label: '20 minutes after', time: { value: '20', offset: 'add' } },
  { label: '30 minutes after', time: { value: '30', offset: 'add' } },
  { label: '40 minutes after', time: { value: '40', offset: 'add' } },
  { label: '50 minutes after', time: { value: '50', offset: 'add' } }
];

export function solarEventRequiredValidator(form: FormGroup) {
  const isStartSolar = form.get('isStartSolarEvent')?.value;
  const startSolar = form.get('startSolarEvent');
  if (isStartSolar) {
    startSolar?.setValidators([Validators.required]);
  } else {
    startSolar?.setValidators(null);
  }

  const isEndSolar = form.get('isEndSolarEvent')?.value;
  const endSolar = form.get('endSolarEvent');

  if (isEndSolar) {
    endSolar?.setValidators([Validators.required]);
  } else {
    endSolar?.setValidators(null);
  }

  return null;
}
